import React from 'react';

/**
 * Catches render/runtime errors in the page tree so one broken page shows a
 * recoverable message instead of blanking the whole app (React unmounts the
 * entire tree on an uncaught error). The sidebar and nav stay alive because
 * this wraps only the routed content.
 *
 * `resetKey` (pass the current pathname) clears the error on navigation, so
 * moving to another page recovers automatically without a manual reload.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface it in the console for debugging (and any future error reporter).
    console.error('Page error:', error, info && info.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 48, maxWidth: 640, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ color: 'var(--text)' }}>This page hit an error</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            The rest of the app is fine — use the sidebar to go elsewhere, or reload this page.
          </p>
          <pre style={{
            textAlign: 'left', background: 'var(--surface-sunken, #1e293b)', color: 'var(--text-secondary)',
            padding: 12, borderRadius: 8, fontSize: 12, overflow: 'auto', marginTop: 16,
          }}>
            {String(this.state.error && (this.state.error.stack || this.state.error.message || this.state.error))}
          </pre>
          <button
            className="btn btn-primary"
            style={{ marginTop: 16 }}
            onClick={() => window.location.reload()}
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
